import { useCallback, useEffect, useState } from "react";

import { ApiError } from "@/lib/api";

/** Normalise an unknown thrown value into an ApiError. */
function toApiError(caught: unknown): ApiError {
  if (caught instanceof ApiError) return caught;
  const message = caught instanceof Error ? caught.message : "Unexpected error";
  return new ApiError(0, message);
}

/** Identity of a request, so a settled result can be matched to its inputs. */
function requestKey(deps: unknown[], nonce: number): string {
  return `${nonce}|${deps.map((d) => String(d)).join("|")}`;
}

interface Snapshot<T> {
  key: string;
  data: T | null;
  error: ApiError | null;
}

export interface AsyncState<T> {
  data: T | null;
  error: ApiError | null;
  loading: boolean;
  /** Re-run the fetcher. */
  refetch: () => void;
}

/**
 * Run an async fetcher and track its state.
 *
 * Deliberately minimal instead of pulling in a data-fetching library: the app has
 * a handful of endpoints and no cross-view cache requirements, and a TA reading
 * this file should not have to learn a query framework first.
 *
 * `deps` behaves like a useEffect dependency list, and the fetcher is read from
 * the render in which the effect fires — so include anything the fetcher closes
 * over (challenge id, API key) in `deps`. Pass `null` as the fetcher for "not
 * ready yet" (e.g. no API key), which yields an idle state rather than a doomed
 * request.
 *
 * `loading` is *derived* by comparing the settled snapshot's key against the
 * current one, rather than stored. Storing it would mean calling setState
 * synchronously inside the effect, which React 19 flags: it forces an extra
 * render pass on every dependency change.
 */
export function useAsync<T>(
  fetcher: (() => Promise<T>) | null,
  deps: unknown[],
): AsyncState<T> {
  const [snapshot, setSnapshot] = useState<Snapshot<T> | null>(null);
  const [nonce, setNonce] = useState(0);

  const enabled = fetcher !== null;
  const key = requestKey(deps, nonce);

  useEffect(() => {
    if (!fetcher) return;
    let cancelled = false;

    fetcher()
      .then((data) => {
        if (!cancelled) setSnapshot({ key, data, error: null });
      })
      .catch((caught: unknown) => {
        if (!cancelled) setSnapshot({ key, data: null, error: toApiError(caught) });
      });

    return () => {
      // Prevents a slow response from a previous render overwriting a newer one,
      // which showed up as the wrong challenge's data after fast navigation.
      cancelled = true;
    };
    // `fetcher` is intentionally excluded: callers pass an inline arrow that is a
    // new reference every render, so depending on it would loop forever. `key`
    // already encodes `deps` and the refetch nonce.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  const refetch = useCallback(() => setNonce((n) => n + 1), []);

  return {
    data: enabled ? (snapshot?.data ?? null) : null,
    error: enabled ? (snapshot?.error ?? null) : null,
    loading: enabled && snapshot?.key !== key,
    refetch,
  };
}

/**
 * Poll `fetcher` until `isDone` returns true, then stop.
 *
 * Used after an upload: the server returns 202 immediately and scores in the
 * background, so the client polls GET /status/{job_id}. Polling also stops on
 * error rather than hammering a failing endpoint.
 */
export function usePolling<T>(
  fetcher: (() => Promise<T>) | null,
  isDone: (value: T) => boolean,
  intervalMs = 1500,
  deps: unknown[] = [],
): AsyncState<T> {
  const [snapshot, setSnapshot] = useState<Snapshot<T> | null>(null);
  const [nonce, setNonce] = useState(0);

  const enabled = fetcher !== null;
  const key = requestKey(deps, nonce);

  useEffect(() => {
    if (!fetcher) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const tick = async () => {
      try {
        const data = await fetcher();
        if (cancelled) return;
        setSnapshot({ key, data, error: null });
        if (!isDone(data)) timer = setTimeout(tick, intervalMs);
      } catch (caught) {
        if (cancelled) return;
        setSnapshot({ key, data: null, error: toApiError(caught) });
      }
    };

    void tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, intervalMs]);

  const refetch = useCallback(() => setNonce((n) => n + 1), []);

  return {
    data: enabled ? (snapshot?.data ?? null) : null,
    error: enabled ? (snapshot?.error ?? null) : null,
    loading: enabled && snapshot?.key !== key,
    refetch,
  };
}

/**
 * Poll `fetcher` for as long as the page is open, at a rate that depends on what came back.
 *
 * `intervalFor` returns the delay before the next request, or null to stop -- until `deps`
 * change or `refetch` is called. Unlike `usePolling` this is for things that may *become*
 * interesting after the page loads: a challenge page is idle until the student's SDK
 * connects, and then wants to notice within a few seconds.
 */
export function useRefreshing<T>(
  fetcher: (() => Promise<T>) | null,
  intervalFor: (value: T) => number | null,
  deps: unknown[] = [],
): AsyncState<T> {
  const [snapshot, setSnapshot] = useState<Snapshot<T> | null>(null);
  const [nonce, setNonce] = useState(0);

  const enabled = fetcher !== null;
  const key = requestKey(deps, nonce);

  useEffect(() => {
    if (!fetcher) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const tick = async () => {
      try {
        const data = await fetcher();
        if (cancelled) return;
        setSnapshot({ key, data, error: null });
        const delay = intervalFor(data);
        if (delay !== null) timer = setTimeout(tick, delay);
      } catch (caught) {
        if (cancelled) return;
        setSnapshot({ key, data: null, error: toApiError(caught) });
      }
    };

    void tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  const refetch = useCallback(() => setNonce((n) => n + 1), []);

  return {
    data: enabled ? (snapshot?.data ?? null) : null,
    error: enabled ? (snapshot?.error ?? null) : null,
    loading: enabled && snapshot?.key !== key,
    refetch,
  };
}
